import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import ClientCombobox from "../components/ClientCombobox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Desktop, HardDrives, WifiHigh, Printer, DeviceMobile, Package, MagnifyingGlass, PencilSimple, Trash, FilePdf, Tag } from "@phosphor-icons/react";

const TYPE_LABEL = {
  pc: "PC", serveur: "Serveur", nas: "NAS", switch: "Switch",
  routeur: "Routeur", imprimante: "Imprimante", telephone: "Téléphone",
  onduleur: "Onduleur", autre: "Autre",
};
const TYPES = Object.keys(TYPE_LABEL);
const ICONS = {
  pc: Desktop, serveur: HardDrives, nas: HardDrives, switch: WifiHigh,
  routeur: WifiHigh, imprimante: Printer, telephone: DeviceMobile,
  onduleur: Package, autre: Package,
};

export default function Equipment() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("__all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    client_id: "", name: "", type: "pc", serial_number: "", ip_address: "",
    location: "", purchase_date: "", warranty_end: "", notes: "",
  });
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (type !== "__all") params.type = type;
    const r = await api.get("/equipment", { params });
    setItems(r.data);
    if (user?.role !== "client") {
      const u = await api.get("/users", { params: { role: "client" } });
      setClients(u.data);
    }
  };
  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line */ }, []);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/modules/flags").then((r) => {
      if (r.data?.inventory === false) { toast.info("Le module Inventaire est désactivé."); navigate("/", { replace: true }); }
    }).catch(() => {});
  }, [navigate]);

  // Auto-open "new equipment" dialog with a preselected client — /inventaire?new=1&client_id=xxx
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      const cid = searchParams.get("client_id") || "";
      setEditing(null);
      setForm({ client_id: cid, name: "", type: "pc", serial_number: "", ip_address: "", location: "", purchase_date: "", warranty_end: "", notes: "" });
      setOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new"); next.delete("client_id");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => { setEditing(null); setForm({ client_id: "", name: "", type: "pc", serial_number: "", ip_address: "", location: "", purchase_date: "", warranty_end: "", notes: "" }); setOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({...e}); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/equipment/${editing.id}`, form);
        toast.success("Équipement mis à jour");
      } else {
        await api.post("/equipment", form);
        toast.success("Équipement créé");
      }
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Retirer "${e.name}" de l'inventaire ?`)) return;
    await api.delete(`/equipment/${e.id}`);
    toast.success("Retiré");
    load();
  };

  const isStaff = user?.role !== "client";

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-caps">Parc informatique</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Inventaire matériel</h1>
          <p className="text-sm text-muted-foreground mt-1">PC, serveurs, NAS, switchs, imprimantes et téléphonie liés à chaque client.</p>
        </div>
        {isStaff && (
          <div className="flex items-center gap-2">
            <Button data-testid="download-labels-btn" variant="secondary" onClick={async () => {
              try {
                const r = await api.get("/inventory/labels.pdf", { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                window.open(url, "_blank", "noopener");
                setTimeout(() => URL.revokeObjectURL(url), 60000);
              } catch (e) { toast.error(e.response?.data?.detail || "Erreur PDF"); }
            }}><Tag size={16} className="mr-2"/> Étiquettes (8/page)</Button>
            <Button data-testid="new-equipment-btn" onClick={openNew}><Plus size={16} className="mr-2"/> Ajouter</Button>
          </div>
        )}
      </header>

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-4 relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
              <Input data-testid="equip-search" className="pl-9" placeholder="Nom, série, IP, emplacement…"
                value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}/>
            </div>
            <div className="md:col-span-2">
              <Select value={type} onValueChange={(v) => { setType(v); setTimeout(load, 0); }}>
                <SelectTrigger data-testid="equip-type-filter"><SelectValue placeholder="Type"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">Tous types</SelectItem>
                  {TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABEL[t]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((eq) => {
          const Icon = ICONS[eq.type] || Package;
          return (
            <Card key={eq.id} data-testid={`equip-${eq.id}`} className="card-hover">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                      <Icon size={20} weight="duotone"/>
                    </div>
                    <div>
                      <Badge variant="outline" className="uppercase text-[0.6rem]">{TYPE_LABEL[eq.type]}</Badge>
                      <h3 className="font-display text-lg font-semibold mt-1 leading-tight">{eq.name}</h3>
                    </div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                  <Info label="Client" value={eq.client_name}/>
                  <Info label="Série" value={eq.serial_number}/>
                  <Info label="IP" value={eq.ip_address} mono/>
                  <Info label="Emplacement" value={eq.location}/>
                  <Info label="Achat" value={eq.purchase_date}/>
                  <Info label="Garantie" value={eq.warranty_end}/>
                </div>
                {eq.notes && <p className="text-xs text-muted-foreground mt-3 line-clamp-2">{eq.notes}</p>}
                {isStaff && (
                  <div className="flex gap-2 mt-4 pt-4 border-t">
                    <Button size="sm" variant="ghost" title="Étiquette PDF" onClick={async () => {
                      try {
                        const r = await api.get(`/equipment/${eq.id}/label.pdf`, { responseType: "blob" });
                        const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                        window.open(url, "_blank", "noopener");
                        setTimeout(() => URL.revokeObjectURL(url), 60000);
                      } catch (e) { toast.error(e.response?.data?.detail || "Erreur PDF"); }
                    }} data-testid={`label-equip-${eq.id}`}><Tag size={14}/></Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(eq)} data-testid={`edit-equip-${eq.id}`}><PencilSimple size={14}/></Button>
                    {user?.role === "admin" && (
                      <Button size="sm" variant="ghost" onClick={() => remove(eq)} data-testid={`del-equip-${eq.id}`}><Trash size={14}/></Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        {items.length === 0 && <p className="text-muted-foreground col-span-full text-center py-10">Aucun équipement.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Modifier" : "Nouvel équipement"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {!editing && (
                <div className="col-span-2">
                  <Label>Client</Label>
                  <ClientCombobox
                    clients={clients}
                    value={form.client_id}
                    onChange={(v) => setForm({...form, client_id: v})}
                    allowNew={false}
                  />
                </div>
              )}
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                  <SelectTrigger data-testid="equip-type"><SelectValue/></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABEL[t]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Nom / identifiant</Label>
              <Input required data-testid="equip-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Ex. PC-COMPTA-01"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>N° de série</Label>
                <Input data-testid="equip-serial" value={form.serial_number || ""} onChange={(e) => setForm({...form, serial_number: e.target.value})}/>
              </div>
              <div>
                <Label>Adresse IP</Label>
                <Input data-testid="equip-ip" value={form.ip_address || ""} onChange={(e) => setForm({...form, ip_address: e.target.value})} placeholder="192.168.1.10"/>
              </div>
              <div>
                <Label>Emplacement</Label>
                <Input data-testid="equip-location" value={form.location || ""} onChange={(e) => setForm({...form, location: e.target.value})} placeholder="Salle serveur, Bureau A…"/>
              </div>
              <div>
                <Label>Date d'achat</Label>
                <Input type="date" data-testid="equip-purchase" value={form.purchase_date || ""} onChange={(e) => setForm({...form, purchase_date: e.target.value})}/>
              </div>
              <div>
                <Label>Fin de garantie</Label>
                <Input type="date" data-testid="equip-warranty" value={form.warranty_end || ""} onChange={(e) => setForm({...form, warranty_end: e.target.value})}/>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea data-testid="equip-notes" value={form.notes || ""} onChange={(e) => setForm({...form, notes: e.target.value})}/>
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" data-testid="equip-save">{editing ? "Enregistrer" : "Créer"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[0.6rem] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-medium truncate ${mono ? "mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
